import React from 'react';

import { Select } from '@aline/common';
import { params } from '@aline/routes';
import { CrossIconButton, PanelHeader } from 'src/components';
import { useCreatePlaybookAndEdit } from 'src/components/admin/playbooks/use-create-playbook-and-edit';
import { useDrawerSearchParam } from 'src/editor/toolbars/right-panel/drawer-search-params';
import { Apollo } from 'src/gql/generated';
import { useDocumentParams } from 'src/helpers';
import { makeStyles } from 'tss-react/mui';

import { PlaybooksPanel } from './PlaybooksPanel';
import { useBackToAI } from '../../BackToAIButton';
import { AIDrawerContainer, AIDrawerFooterContainer } from '../AiDrawer';
import { AlineDocumentGuard } from '../shared/AlineDocumentGuard';

const useStyles = makeStyles()({
  addButton: {
    height: 36,
  },
});

/**
 * The Playbooks drawer: every playbook the org has, with its run status against
 * the open document, and an in-panel detail view for running and reviewing.
 * Rules are edited on the Settings playbook page; "Create Playbook" makes an
 * empty playbook and opens it there. The button belongs to the list, so it is
 * hidden while a playbook is open (`?playbookId=` set).
 *
 * Registers OnDocPlaybooksUpdated once for the open document. Apollo writes
 * the payload into the normalized cache; the per-playbook queries on the list
 * and detail views pick the updates up from there.
 */
export const PlaybooksDrawerContent = React.memo(function PlaybooksDrawerContent(): JSX.Element {
  const { classes } = useStyles();
  const handleBackToAI = useBackToAI();
  const { documentId } = useDocumentParams();
  const { createPlaybookAndEdit, loading } = useCreatePlaybookAndEdit();
  const { value: openPlaybookId } = useDrawerSearchParam(params.rightPanel.playbookId);

  Apollo.useOnDocPlaybooksUpdatedSubscription({
    shouldResubscribe: true,
    variables: { documentId },
  });

  return (
    <AIDrawerContainer>
      <AlineDocumentGuard title="Playbooks">
        {!openPlaybookId && (
          <PanelHeader
            showBackButton
            backButtonTooltip="Back to AI"
            text="Playbooks"
            onClickBack={handleBackToAI}
          />
        )}
        <PlaybooksPanel />
        {!openPlaybookId && (
          <AIDrawerFooterContainer>
            <CrossIconButton
              buttonClassName={classes.addButton}
              color="primary"
              data-cy={Select.document.editor.rightDrawer.ai.panel.addPlaybookButton}
              loading={loading}
              text="Create Playbook"
              onClick={createPlaybookAndEdit}
            />
          </AIDrawerFooterContainer>
        )}
      </AlineDocumentGuard>
    </AIDrawerContainer>
  );
});
